import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Cookies from "universal-cookie";
import HomeIcon from "@mui/icons-material/Home";
import ContinueButton from "./ContinueButton";
import { utilities } from "@/_helpers";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import logo from "../../_images/logos/LogoLimited.png";
import PamV3 from "../../_images/PAMv3.png";
import Iid from "../../_images/L250.png";
import ISA from "../../_images/ISA.png";
import { brandColors } from "../../_components/brandedComponent/BrandedColors";
import { LifeSafer } from "../../_helpers/BrandConstants";

const Device = ({
  submitting,
  setDeviceLocationType,
  deviceLocationType,
  isLoaded,
  goToBackStep,
  setGoogleMapData,
  setStepSchedule,
  stepSchedule,
  STEPS,
  setActiveStep,
  setOpen,
  jumpedMap,
  availableDeviceTypes,
  requireExplicitSelection = false,
}) => {
  const { t } = useTranslation();
  const cookies = new Cookies();
  const lifeSaferUser = cookies.get("EnrollNow") || {};
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
  const enrollmentCode = cookies.get("lsEnrollmentCode");
  const url = window.location.href;
  const deviceFromCookie = lifeSaferUser?.DeviceType;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;
  const siteCountry = cookies.get("siteCountry") || "USA";

  const resolvedDeviceType = useMemo(() => {
    if (requireExplicitSelection && !deviceLocationType) return null;
    if (!deviceFromCookie && !deviceLocationType) return null;
    return (
      utilities.deviceInfo(
        requireExplicitSelection
          ? deviceLocationType
          : deviceFromCookie || deviceLocationType,
      )
        ?.deviceType ?? null
    );
  }, [deviceFromCookie, deviceLocationType, requireExplicitSelection]);

  const isISA =
    url.includes("lifesaferisa") ||
    resolvedDeviceType === "ISA" ||
    resolvedDeviceType === "Speed";

  const DEFAULT_DEVICE = isISA ? "Speed" : null;
  const FALLBACK_DEVICE_TYPES = ["Interlock", "Portable", "Speed"];

  const isLifeSaferia = url.toLowerCase().includes("lifesaferisa");

  const {
    handleSubmit,
    control,
    watch,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      deviceType: requireExplicitSelection
        ? null
        : resolvedDeviceType ||
        (enrolleeUser?.clientTypeId === 5 || enrolleeUser?.deviceType === "Portable" || enrolleeUser?.deviceType === "PAM"
          ? "Portable"
          : enrolleeUser?.clientTypeId === 6 || enrolleeUser?.deviceType === "Speed" || enrolleeUser?.deviceType === "ISA"
            ? "Speed"
            : enrolleeUser?.clientTypeId === 1 || enrolleeUser?.deviceType === "Interlock" || enrolleeUser?.deviceType === "IID"
              ? "Interlock"
              : null) ||
        DEFAULT_DEVICE,
      zip: sessionStorage.getItem("zipCode") || "",
    },
  });

  const DEVICE_TYPE_MAP = {
    Interlock: t("steps.iid"),
    Portable: t("steps.pam"),
    Speed: t("steps.isa"),
    ISA: t("steps.isa"),
  };

  const deviceLoopData = [
    {
      id: 1,
      title: t("steps.iid"),
      description: t("steps.iidDescription"),
      type: "Interlock",
      image: Iid,
    },
    {
      id: 3,
      title: t("steps.isa"),
      description: t("steps.isaDescription"),
      type: "Speed",
      image: ISA,
    },
    {
      id: 2,
      title: t("steps.pam"),
      description: t("steps.pamDescription"),
      type: "Portable",
      image: PamV3,
    },
  ];

  const selectedDevice = watch("deviceType");
  const watchedZip = watch("zip");

  const [deviceTypes, setDeviceTypes] = useState([]);
  const [deviceLoops, setDeviceLoops] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showHelpIcon, setShowHelpIcon] = useState(false);
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const regionCode = countryCode;

  useEffect(() => {
    if (!resolvedDeviceType) return;
    const resolvedClientTypeId =
      utilities.deviceInfo(resolvedDeviceType)?.deviceTypeId;

    sessionStorage.setItem(
      "enrolleeUser",
      JSON.stringify({
        ...enrolleeUser,
        deviceType: resolvedDeviceType,
        clientTypeId: resolvedClientTypeId,
        ClientTypeId: resolvedClientTypeId,
        clientTypeID: resolvedClientTypeId,
        ClientTypeID: resolvedClientTypeId,
      }),
    );
  }, [resolvedDeviceType]);

  // useEffect(() => {
  //   if (enrollmentCode?.deviceTypes) {
  //     const arr = enrollmentCode.deviceTypes.split(",");
  //     setDeviceTypes(arr);
  //     if (arr.length === 1 && !resolvedDeviceType) {
  //       setValue("deviceType", arr[0]);
  //       clearErrors("deviceType");
  //     }
  //   }
  // }, [enrollmentCode?.deviceTypes, resolvedDeviceType]);

  useEffect(() => {
    let nextDeviceTypes = [];
    if (requireExplicitSelection) {
      nextDeviceTypes = availableDeviceTypes?.length
        ? availableDeviceTypes
        : FALLBACK_DEVICE_TYPES;
    } else if (availableDeviceTypes?.length) {
      nextDeviceTypes = availableDeviceTypes;
    } else if (isLifeSaferia) {
      nextDeviceTypes = ["Speed"];
      setDeviceTypes(nextDeviceTypes);
      setValue("deviceType", nextDeviceTypes[0]);
      return;
    }

    if (enrollmentCode?.deviceTypes) {
      nextDeviceTypes = enrollmentCode.deviceTypes.split(",");

      if (isISA) {
        nextDeviceTypes = ["Speed"];
      }
    } else if (resolvedDeviceType) {
      nextDeviceTypes = [resolvedDeviceType];
    } else {
      nextDeviceTypes = FALLBACK_DEVICE_TYPES;
    }

    setDeviceTypes(nextDeviceTypes);

    if (
      !requireExplicitSelection &&
      !resolvedDeviceType &&
      nextDeviceTypes.length === 1
    ) {
      setValue("deviceType", nextDeviceTypes[0]);
      clearErrors("deviceType");
    }
  }, [
    availableDeviceTypes,
    enrollmentCode?.deviceTypes,
    resolvedDeviceType,
    isISA,
    requireExplicitSelection,
  ]);

  useEffect(() => {
    if (!deviceTypes?.length) {
      setDeviceLoops(null);
      setShowHelpIcon(false);
      return;
    }

    const filtered = deviceLoopData.filter((x) =>
      deviceTypes.some((device) => {
        const deviceLower = (device || "").toLowerCase();
        const typeLower = x.type.toLowerCase();

        if (deviceLower === "isa") {
          return ["isa", "speed"].includes(typeLower);
        }

        return deviceLower === typeLower;
      }),
    );

    setDeviceLoops(filtered);
    setShowHelpIcon(filtered.length > 1);
  }, [deviceTypes, t]);

  const onSubmitDevice = ({ deviceType }) => {
    const selectedClientTypeId = utilities.deviceInfo(deviceType)?.deviceTypeId;

    setDeviceLocationType(deviceType);
    sessionStorage.setItem(
      "enrolleeUser",
      JSON.stringify({
        ...enrolleeUser,
        deviceType: deviceType,
        clientTypeId: selectedClientTypeId,
        ClientTypeId: selectedClientTypeId,
        clientTypeID: selectedClientTypeId,
        ClientTypeID: selectedClientTypeId,
        ...(requireExplicitSelection && {
          deviceSelectionConfirmed: true,
          deviceSelectionSource: "user",
        }),
      }),
    );
  };

  const validateZip = (value) => {
    if (value?.length == 0) {
      return t("body.zipRequired");
    }

    if (/^[0-9]{5}$/.test(value)) {
      return true;
    }

    return t("steps.zipInvalid");
  };

  useEffect(() => {
    const jwtToken = cookies.get("lifesaferEnrollment")?.jwtToken;
    if (!jwtToken || !resolvedDeviceType) return;

    const savedZip = sessionStorage.getItem("zipCode");
    if (savedZip) return;

    const enrollNow = cookies.get("EnrollNow") || {};
    const locationIdFromCookie = enrollNow?.LocationId || enrollNow?.LocationID;

    if (locationIdFromCookie && !jumpedMap) {
      setStepSchedule(STEPS.DATE_TIME);
      return;
    }

    if (!navigator.geolocation) {
      setStepSchedule(STEPS.LOCATION_ZIP);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      // () => setStepSchedule(STEPS.SCHEDULE_NOW),
      () => setStepSchedule(STEPS.LOCATION_ZIP),
    );
  }, [resolvedDeviceType]);

  const onSubmitZip = async ({ zip }) => {
    try {
      const locationData = await withTimeout(getLocation(zip), 5000);
      const parsed = processGeocodeResponse(locationData);

      if (!parsed) {
        setStepSchedule(STEPS.LOCATION_ZIP);
        return;
      }

      sessionStorage.setItem("zipCode", zip);
      sessionStorage.setItem(
        "googleMapData",
        JSON.stringify({
          homeLat: parsed.lat,
          homeLong: parsed.lng,
          homeAddr1: parsed.addr1,
          homeAddr2: parsed.addr2,
          city: parsed.city,
          county: parsed.county,
          state: parsed.state,
          zip,
        }),
      );

      setGoogleMapData({
        homeLat: parsed.lat,
        homeLong: parsed.lng,
        homeAddr1: parsed.addr1,
        homeAddr2: parsed.addr2,
        city: parsed.city,
        county: parsed.county,
        state: parsed.state,
      });
      sessionStorage.removeItem("forceLocationZipAfterVehicle");
      setActiveStep(0);
      setStepSchedule(STEPS.SCHEDULE_NOW);
      setOpen(true);
    } catch {
      setStepSchedule(STEPS.LOCATION_ZIP);
    }
  };

  const processGeocodeResponse = (response) => {
    if (!response?.results?.length) return null;
    const r = response.results[0];
    const c = r.address_components;
    const get = (t) => c.find((x) => x.types.includes(t))?.long_name;
    return {
      lat: r.geometry.location.lat(),
      lng: r.geometry.location.lng(),
      addr1: r.formatted_address?.split(",")[0] ?? null,
      addr2: get("subpremise") ?? null,
      city: get("locality") ?? null,
      county: get("administrative_area_level_2") ?? null,
      state:
        c.find((x) => x.types.includes("administrative_area_level_1"))
          ?.short_name ?? null,
    };
  };

  const getLocation = async (address) => {
    if (
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Geocoder ||
      !isLoaded
    ) {
      return [];
    }
    const geocoder = new window.google.maps.Geocoder();
    let sCountry = "USA";
    if (siteCountry === "Australia") {
      sCountry = "Australia";
    }
    return geocoder.geocode({
      address: `${address},${sCountry}`,
      region: regionCode || "US",
    });
  };

  function withTimeout(promise, timeout = 5000) {
    let timer;
    const timeoutPromise = new Promise((_, reject) => {
      timer = setTimeout(() => reject(), timeout);
    });
    return Promise.race([promise, timeoutPromise]).finally(() =>
      clearTimeout(timer),
    );
  }

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
  };

  const changeDeviceNeeded = (deviceNeeded) => {
    setValue("deviceType", deviceNeeded);
    clearErrors("deviceType");
    setOpenDrawer(false);
  };

  if (stepSchedule === STEPS.DEVICE) {
    return (
      <>
        <form onSubmit={handleSubmit(onSubmitDevice)}>
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid sx={{ textAlign: "center" }}>
              <Typography variant="h5" fontWeight="bold" color="#4da3ff">
                {t("location.welcomeLifesafer")}
              </Typography>
              <Typography>{t("location.deviceSchedule")}</Typography>
            </Grid>
            <Grid width={300}>
              <Controller
                name="deviceType"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    options={deviceTypes}
                    disableClearable
                    getOptionLabel={(option) =>
                      DEVICE_TYPE_MAP[option] || option
                    }
                    onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t("location.device")}
                        placeholder={t("location.chooseDeviceType")}
                        // error={!!fieldState.error}
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
                rules={{
                  required: t("location.deviceTypeRequired"),
                }}
              />
            </Grid>
            <ContinueButton
              type="submit"
              submitting={submitting}
              disabled={!selectedDevice}
              continueText="location.bookAppointment"
            />
          </Grid>
        </form>

        {isMobile && showHelpIcon && deviceTypes?.length > 1 && (
          <Grid
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              position: "relative",
              mt: -5,
            }}
          >
            <HelpIcon
              color="primary"
              sx={{
                position: "absolute",
                bottom: "2px",
              }}
              onClick={toggleDrawer(true)}
            />
          </Grid>
        )}

        <Drawer anchor="bottom" open={openDrawer} onClose={toggleDrawer(false)}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              borderTop: "2px solid #1c355e",
            }}
          >
            <Box
              sx={{
                width: "95%",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1,
              }}
            >
              <Grid sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <img
                  src={logo}
                  alt="logo"
                  style={{
                    height: isMobileSmall ? "26px" : "18px",
                    width: isMobileSmall ? "150px" : "100px",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: brandColors[brand].primary,
                    mt: isMobileSmall ? "8px" : "2px",
                    fontStyle: "italic",
                    fontSize: isMobileSmall ? "24px" : "20px",
                  }}
                >
                  {t("steps.devices")}
                </Typography>
              </Grid>
              <IconButton
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "red",
                }}
              >
                <CloseIcon
                  sx={{
                    fontSize: "18px",
                  }}
                  onClick={toggleDrawer(false)}
                />
              </IconButton>
            </Box>
            <Divider
              sx={{
                height: "1px",
                width: "100%",
                backgroundColor: "#E9E9E9",
                opacity: 1,
                border: "none",
              }}
            />

            {deviceLoops?.map((item) => (
              <Grid key={item?.id}>
                <Box sx={{ display: "flex", width: "100%" }}>
                  <Grid
                    sx={{
                      width: "35%",
                      padding: "4px",
                      paddingLeft: item?.id === 3 ? "10px" : "20px",
                      marginTop: "10px",
                    }}
                  >
                    <img
                      src={item?.image}
                      style={{ height: item?.id === 3 ? 62 : 85 }}
                    />
                  </Grid>
                  <Grid sx={{ width: "65%", padding: "4px" }}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        marginTop: 1,
                      }}
                    >
                      {item?.title}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", padding: "4px 0px" }}>
                      {item?.description}
                    </Typography>
                    <Grid
                      sx={{
                        textDecoration: "underline",
                        fontSize: "14px",
                        paddingTop: "6px",
                        paddingBottom: "8px",
                        color: "#4B9CD3",
                        fontWeight: "450",
                        textUnderlineOffset: "4px",
                      }}
                      onClick={() => changeDeviceNeeded(item?.type)}
                    >
                      {t("steps.chooseThisDevice")}
                    </Grid>
                  </Grid>
                </Box>
                <Grid
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Divider
                    sx={{
                      height: "3px",
                      width: "90%",
                      backgroundColor: "#E9E9E9",
                      opacity: 1,
                      border: "none",
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Box>
        </Drawer>
      </>
    );
  }

  if (stepSchedule === STEPS.LOCATION_ZIP) {
    return (
      <>
        <Grid
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#4b9CD3",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {t("steps.lifeSaferLocation")}
          </Typography>
        </Grid>
        <form
          onSubmit={handleSubmit(onSubmitZip)}
          style={{ marginTop: "24px" }}
        >
          <Grid container direction="column" alignItems="center" spacing={2}>
            <TextField
              label={t("steps.homeZip")}
              placeholder={t("location.enterZipCode")}
              helperText={errors.zip ? errors.zip.message : ""}
              sx={{ width: 300 }}
              error={!!errors.zip}
              {...register("zip", {
                validate: (value) => {
                  if (value?.length === 0 || value === null) {
                    return t("body.zipRequired");
                  }
                  return validateZip(value);
                },
              })}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "").slice(0, 5);
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <HomeIcon /> }}
            />
            <ContinueButton
              submitting={submitting}
              disabled={!watchedZip || watchedZip.length !== 5}
              goToBackStep={goToBackStep}
            />
          </Grid>
        </form>
      </>
    );
  }

  return null;
};

Device.propTypes = {
  submitting: PropTypes.bool,
  setDeviceLocationType: PropTypes.func.isRequired,
  deviceLocationType: PropTypes.string,
  isLoaded: PropTypes.bool,
  goToBackStep: PropTypes.func,
  setGoogleMapData: PropTypes.func,
  setStepSchedule: PropTypes.func.isRequired,
  stepSchedule: PropTypes.string,
  STEPS: PropTypes.object.isRequired,
  setActiveStep: PropTypes.func,
  setOpen: PropTypes.func,
  jumpedMap: PropTypes.bool,
  availableDeviceTypes: PropTypes.arrayOf(PropTypes.string),
  requireExplicitSelection: PropTypes.bool,
};

export default Device;
